import { Box, NumberInput, Select, Textarea } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as AutoStatementApi from "../../apis/auto-statement";
import { message } from "../../utils/Message";
import { countChanges, MasterCard, OffCard, SaveBar, SectionCard, SettingRow, settingsClasses, SwitchRow, unitProps } from "./settings-ui";
import type { SettingsTabProps } from "./settings-ui";
import { useAutoStatementConfigData } from "./use-settings-config";

interface IAutoStatementConfig {
  autoStatementEnabled: boolean;
  generateDayOfMonth: number | string;
  scopeType: string | null;
  minimumBalance: number | string;
  autoSend: boolean;
  sendMethod: string | null;
  includeLateFeeDetail: boolean;
  sellerDefaultNotes: string;
  reminderEnabled: boolean;
  reminderAfterDays: number | string;
  maxReminders: number | string;
}

const initialConfig: IAutoStatementConfig = {
  autoStatementEnabled: false,
  generateDayOfMonth: "",
  scopeType: null,
  minimumBalance: "",
  autoSend: false,
  sendMethod: null,
  includeLateFeeDetail: false,
  sellerDefaultNotes: "",
  reminderEnabled: false,
  reminderAfterDays: "",
  maxReminders: "",
};

const useScopeTypeOptions = (t: (key: string) => string) => [
  { value: "ALL_BUYERS", label: t("autoStatement.scopeType.allBuyers") },
  { value: "SELECTED_BUYERS", label: t("autoStatement.scopeType.selectedBuyers") },
  { value: "ACTIVE_ONLY", label: t("autoStatement.scopeType.activeOnly") },
];

const useSendMethodOptions = (t: (key: string) => string) => [
  { value: "PORTAL", label: t("autoStatement.sendMethod.portal") },
  { value: "EMAIL", label: t("autoStatement.sendMethod.email") },
  { value: "BOTH", label: t("autoStatement.sendMethod.both") },
];

const fromServer = (data: any): IAutoStatementConfig => ({
  autoStatementEnabled: data.autoStatementEnabled ?? false,
  generateDayOfMonth: data.generateDayOfMonth ?? "",
  scopeType: data.scopeType ?? null,
  minimumBalance: data.minimumBalance ?? "",
  autoSend: data.autoSend ?? false,
  sendMethod: data.sendMethod ?? null,
  includeLateFeeDetail: data.includeLateFeeDetail ?? false,
  sellerDefaultNotes: data.sellerDefaultNotes ?? "",
  reminderEnabled: data.reminderEnabled ?? false,
  reminderAfterDays: data.reminderAfterDays ?? "",
  maxReminders: data.maxReminders ?? "",
});

export function AutoStatementConfig({ onDirtyChange }: SettingsTabProps) {
  const { t } = useTranslation("settings");
  const scopeTypeOptions = useScopeTypeOptions(t);
  const sendMethodOptions = useSendMethodOptions(t);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [config, setConfig] = useState<IAutoStatementConfig>(initialConfig);
  const [saved, setSaved] = useState<IAutoStatementConfig>(initialConfig);

  const { data, isLoading, mutate } = useAutoStatementConfigData();

  useEffect(() => {
    if (!data) return;
    const next = fromServer(data);
    setConfig(next);
    setSaved(next);
  }, [data]);

  const handleChange = (field: keyof IAutoStatementConfig, value: any) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!config.autoStatementEnabled) return true;
    if (!config.generateDayOfMonth || config.generateDayOfMonth === "") newErrors.generateDayOfMonth = t("autoStatement.generate.required");
    if (!config.scopeType) newErrors.scopeType = t("autoStatement.scopeType.required");
    if (config.autoSend && !config.sendMethod) newErrors.sendMethod = t("autoStatement.sendMethod.required");
    if (config.reminderEnabled) {
      if (!config.reminderAfterDays || config.reminderAfterDays === "") newErrors.reminderAfterDays = t("autoStatement.reminderAfterDays.required");
      if (!config.maxReminders || config.maxReminders === "") newErrors.maxReminders = t("autoStatement.maxReminders.required");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) { message.error(t("autoStatement.validationError")); return; }
    try {
      setSaving(true);
      // Backend Joi талбарууд optional/default-гүй, logic нь config.set(...) хийдэг тул орхисон талбар хуучин утгаараа
      // үлддэг. Тиймээс цэвэрлэж болох талбарыг backend-ийн зөвшөөрдөг утгаар илгээнэ; заавал бөглөх талбарыг
      // validate() шалгасан, Joi "" тоог хүлээж авдаггүй тул хоосон бол орхино.
      const payload = {
        autoStatementEnabled: config.autoStatementEnabled,
        ...(config.generateDayOfMonth !== "" && { generateDayOfMonth: config.generateDayOfMonth }),
        ...(config.scopeType && { scopeType: config.scopeType }),
        minimumBalance: config.minimumBalance === "" ? 0 : config.minimumBalance,
        autoSend: config.autoSend,
        ...(config.sendMethod && { sendMethod: config.sendMethod }),
        includeLateFeeDetail: config.includeLateFeeDetail,
        sellerDefaultNotes: config.sellerDefaultNotes === "" ? null : config.sellerDefaultNotes,
        reminderEnabled: config.reminderEnabled,
        ...(config.reminderAfterDays !== "" && { reminderAfterDays: config.reminderAfterDays }),
        ...(config.maxReminders !== "" && { maxReminders: config.maxReminders }),
      };
      await AutoStatementApi.updateConfig(payload);
      // Сервер буцаасан утгаар шинэчилнэ — UI нь DB-ээс зөрөхгүй.
      const fresh = await mutate();
      if (fresh) {
        const next = fromServer(fresh);
        setConfig(next);
        setSaved(next);
      } else {
        setSaved(config);
      }
      message.success(t("autoStatement.saveSuccess"));
    } catch (err: any) {
      message.error(err?.message || t("autoStatement.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const changes = countChanges(config, saved);
  useEffect(() => onDirtyChange(changes), [changes, onDirtyChange]);
  useEffect(() => () => onDirtyChange(0), [onDirtyChange]);

  const field = { size: "sm", radius: "md" } as const;

  return (
    <Box className={isLoading ? settingsClasses.loading : undefined}>
      <Box className={settingsClasses.main}>
        {!config.autoStatementEnabled ? (
          <OffCard
            title={t("autoStatement.off.title")}
            description={t("autoStatement.off.description")}
            enableLabel={t("autoStatement.off.enable")}
            onEnable={() => handleChange("autoStatementEnabled", true)}
            features={[
              { title: t("autoStatement.off.featureMonthly"), description: t("autoStatement.off.featureMonthlyText") },
              { title: t("autoStatement.off.featureSend"), description: t("autoStatement.off.featureSendText") },
              { title: t("autoStatement.off.featureRemind"), description: t("autoStatement.off.featureRemindText") },
            ]}
          />
        ) : (
          <>
            <MasterCard
              title={t("autoStatement.master.title")}
              subtitle={t("autoStatement.master.subtitle")}
              checked={config.autoStatementEnabled}
              onChange={(v) => handleChange("autoStatementEnabled", v)}
            />

            <SectionCard title={t("autoStatement.section.generate")} subtitle={t("autoStatement.section.generateSubtitle")}>
              <SettingRow label={t("autoStatement.generate.label")} description={t("autoStatement.generate.description")} required>
                <NumberInput
                  aria-label={t("autoStatement.generate.label")}
                  placeholder={t("autoStatement.generate.placeholder")}
                  value={config.generateDayOfMonth}
                  onChange={(v) => handleChange("generateDayOfMonth", v)}
                  min={1} max={28}
                  error={errors.generateDayOfMonth}
                  {...unitProps(t("unit.dayOfMonth"))}
                  {...field}
                />
              </SettingRow>
              <SettingRow label={t("autoStatement.scopeType.label")} description={t("autoStatement.scopeType.description")} required>
                <Select
                  aria-label={t("autoStatement.scopeType.label")}
                  placeholder={t("autoStatement.scopeType.placeholder")}
                  data={scopeTypeOptions}
                  value={config.scopeType}
                  onChange={(v) => handleChange("scopeType", v)}
                  clearable
                  error={errors.scopeType}
                  {...field}
                />
              </SettingRow>
              <SettingRow label={t("autoStatement.minimumBalance.label")} description={t("autoStatement.minimumBalance.description")}>
                <NumberInput
                  aria-label={t("autoStatement.minimumBalance.label")}
                  placeholder={t("autoStatement.minimumBalance.placeholder")}
                  value={config.minimumBalance}
                  onChange={(v) => handleChange("minimumBalance", v)}
                  min={0} thousandSeparator=","
                  {...unitProps("₮")}
                  {...field}
                />
              </SettingRow>
            </SectionCard>

            <SectionCard title={t("autoStatement.section.send")} subtitle={t("autoStatement.section.sendSubtitle")}>
              <SwitchRow
                label={t("autoStatement.autoSend.label")}
                description={t("autoStatement.autoSend.description")}
                checked={config.autoSend}
                onChange={(v) => handleChange("autoSend", v)}
              />
              {config.autoSend && (
                <SettingRow label={t("autoStatement.sendMethod.label")} required>
                  <Select
                    aria-label={t("autoStatement.sendMethod.label")}
                    placeholder={t("autoStatement.sendMethod.placeholder")}
                    data={sendMethodOptions}
                    value={config.sendMethod}
                    onChange={(v) => handleChange("sendMethod", v)}
                    clearable
                    error={errors.sendMethod}
                    {...field}
                  />
                </SettingRow>
              )}
            </SectionCard>

            <SectionCard title={t("autoStatement.section.content")} subtitle={t("autoStatement.section.contentSubtitle")}>
              <SwitchRow
                label={t("autoStatement.includeLateFeeDetail.label")}
                description={t("autoStatement.includeLateFeeDetail.description")}
                checked={config.includeLateFeeDetail}
                onChange={(v) => handleChange("includeLateFeeDetail", v)}
              />
              <SettingRow label={t("autoStatement.sellerNotes.label")} description={t("autoStatement.sellerNotes.description")} stacked>
                <Textarea
                  aria-label={t("autoStatement.sellerNotes.label")}
                  placeholder={t("autoStatement.sellerNotes.placeholder")}
                  value={config.sellerDefaultNotes}
                  onChange={(e) => handleChange("sellerDefaultNotes", e.currentTarget.value)}
                  minRows={3} autosize
                  {...field}
                />
              </SettingRow>
            </SectionCard>

            <SectionCard title={t("autoStatement.section.reminder")} subtitle={t("autoStatement.section.reminderSubtitle")}>
              <SwitchRow
                label={t("autoStatement.reminderEnabled.label")}
                description={t("autoStatement.reminderEnabled.description")}
                checked={config.reminderEnabled}
                onChange={(v) => handleChange("reminderEnabled", v)}
              />
              {config.reminderEnabled && (
                <>
                  <SettingRow label={t("autoStatement.reminderAfterDays.label")} description={t("autoStatement.reminderAfterDays.description")} required>
                    <NumberInput
                      aria-label={t("autoStatement.reminderAfterDays.label")}
                      placeholder={t("autoStatement.reminderAfterDays.placeholder")}
                      value={config.reminderAfterDays}
                      onChange={(v) => handleChange("reminderAfterDays", v)}
                      min={1} max={30}
                      error={errors.reminderAfterDays}
                      {...unitProps(t("unit.days"))}
                      {...field}
                    />
                  </SettingRow>
                  <SettingRow label={t("autoStatement.maxReminders.label")} description={t("autoStatement.maxReminders.description")} required>
                    <NumberInput
                      aria-label={t("autoStatement.maxReminders.label")}
                      placeholder={t("autoStatement.maxReminders.placeholder")}
                      value={config.maxReminders}
                      onChange={(v) => handleChange("maxReminders", v)}
                      min={1} max={5}
                      error={errors.maxReminders}
                      {...unitProps(t("unit.times"))}
                      {...field}
                    />
                  </SettingRow>
                </>
              )}
            </SectionCard>
          </>
        )}

        <SaveBar count={changes} saving={saving} onSave={handleSave} onCancel={() => { setConfig(saved); setErrors({}); }} />
      </Box>
    </Box>
  );
}
